import React, { createContext, useContext, useState } from 'react';

interface Customer {
  id: string;
  name: string;
  legal_name: string;
  business_identification_number?: string;
  domain?: string;
}

interface CustomerRelationship {
  id: string;
  customer_id: string;
  child_id: string;
  relationship?: string;
}

interface SubscriptionPlanPrice {
  id: string;
  subscription_plan_id: string;
  currency: string;
  price_amount: number;
  cost_amount?: number;
  margin?: number;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  is_active: boolean;
  billing_frequency?: string;
  family_id?: string;
  sku?: string;
  term?: string;
  description?: string;
  prices?: SubscriptionPlanPrice[];
}

// Export the CustomerContact interface
export interface CustomerContact {
  id: string;
  customer_id: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone: string | null;
}

// Export the SelectedContact interface as well
export interface SelectedContact {
  contact_id: string;
  contact_type: 'technical' | 'administrative' | 'billing' | 'pedagogical';
  contact: CustomerContact;
}

interface SelectedPlan {
  subscription_plan_id: string;
  subscription_plan: SubscriptionPlan;
  quantity: number;
  unit_price: number;
  current_term_start: string;
  current_term_end: string;
  billing_start_terms?: 'immediately' | 'custom date';
  auto_renew?: boolean;
  billing_cycles?: number;
  billing_by?: string;
  term_start?: string;
  term_end?: string;
}

interface OpportunityWizardState {
  // Step 1: Customer
  selectedCustomer: Customer | null;
  
  // Step 2: Contacts
  selectedContacts: SelectedContact[];
  
  // Step 3: Opportunity Details
  startDate: string;
  currencyCode: string;
  opportunityType: string;
  stage: string;
  value: number;
  closeProbability: number;
  
  // Step 4: Plans
  selectedPlans: SelectedPlan[];
}

interface OpportunityWizardContextType {
  state: OpportunityWizardState;
  setSelectedCustomer: (customer: Customer | null) => void;
  setOpportunityContacts: (contacts: SelectedContact[]) => void;
  setOpportunityDetails: (details: {
    startDate?: string;
    currencyCode?: string;
    opportunityType?: string;
    stage?: string;
    value?: number;
    closeProbability?: number;
  }) => void;
  addSelectedPlan: (plan: SelectedPlan) => void;
  updateSelectedPlan: (index: number, plan: Partial<SelectedPlan>) => void;
  removeSelectedPlan: (index: number) => void;
  resetWizard: () => void;
}

const defaultState: OpportunityWizardState = {
  selectedCustomer: null,
  selectedContacts: [],
  startDate: new Date().toISOString().split('T')[0],
  currencyCode: 'BRL',
  opportunityType: '',
  stage: 'Qualification',
  value: 0,
  closeProbability: 20,
  selectedPlans: [],
};

const OpportunityWizardContext = createContext<OpportunityWizardContextType | undefined>(undefined);

export const OpportunityWizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<OpportunityWizardState>(defaultState);

  const setSelectedCustomer = (customer: Customer | null) => {
    setState((prev) => ({
      ...prev,
      selectedCustomer: customer,
    }));
  };

  const setOpportunityContacts = (contacts: SelectedContact[]) => {
    setState((prev) => ({
      ...prev,
      selectedContacts: contacts,
    }));
  };

  const setOpportunityDetails = (details: {
    startDate?: string;
    currencyCode?: string;
    opportunityType?: string;
    stage?: string;
    value?: number;
    closeProbability?: number;
  }) => {
    setState((prev) => ({
      ...prev,
      startDate: details.startDate || prev.startDate,
      currencyCode: details.currencyCode || prev.currencyCode,
      opportunityType: details.opportunityType || prev.opportunityType,
      stage: details.stage || prev.stage,
      value: details.value !== undefined ? details.value : prev.value,
      closeProbability: details.closeProbability !== undefined ? details.closeProbability : prev.closeProbability,
    }));
  };

  const addSelectedPlan = (plan: SelectedPlan) => {
    setState((prev) => ({
      ...prev,
      selectedPlans: [...prev.selectedPlans, plan],
    }));
  };

  const updateSelectedPlan = (index: number, plan: Partial<SelectedPlan>) => {
    setState((prev) => {
      const updatedPlans = [...prev.selectedPlans];
      updatedPlans[index] = { ...updatedPlans[index], ...plan };
      return {
        ...prev,
        selectedPlans: updatedPlans,
      };
    });
  };

  const removeSelectedPlan = (index: number) => {
    setState((prev) => {
      const updatedPlans = [...prev.selectedPlans];
      updatedPlans.splice(index, 1);
      return {
        ...prev,
        selectedPlans: updatedPlans,
      };
    });
  };

  const resetWizard = () => {
    setState(defaultState);
  };

  return (
    <OpportunityWizardContext.Provider
      value={{
        state,
        setSelectedCustomer,
        setOpportunityContacts,
        setOpportunityDetails,
        addSelectedPlan,
        updateSelectedPlan,
        removeSelectedPlan,
        resetWizard,
      }}
    >
      {children}
    </OpportunityWizardContext.Provider>
  );
};

export const useOpportunityWizard = () => {
  const context = useContext(OpportunityWizardContext);
  if (!context) {
    throw new Error('useOpportunityWizard must be used within a OpportunityWizardProvider');
  }
  return context;
}; 